export type Tag = {
  tagId: number;
  title: string;
};

// TODO: Add test for these classes
export class CommunityPost {
  constructor(
    public communityPostId: number,
    public created: Date,
    public updated: Date,
    public fileId: number,
    public owner: CommunityOwner,
    public isRemix: boolean,
    public parent: ParentCommunityPost | null,
    public root: ParentCommunityPost | null,
    public communityRevisions: CommunityRevision[],
    public totalRemixCount: number,
    public publishedRemixCount: number,
    public reactionCount: number,
    // TODO: this is temporary while we support file based lookup
    public tags: string[]
  ) {}

  static fromAPI(data: any): CommunityPost {
    return new CommunityPost(
      data.community_post_id,
      new Date(data.created),
      new Date(data.updated),
      data.file_id,
      CommunityOwner.fromAPI(data.owner),
      data.is_remix ?? false,
      data.parent ? ParentCommunityPost.fromAPI(data.parent) : null,
      data.root ? ParentCommunityPost.fromAPI(data.root) : null,
      data.community_revisions.map((community_revision: any) =>
        CommunityRevision.fromAPI(community_revision)
      ),
      data.total_remix_count,
      data.published_remix_count,
      data.reaction_count,
      data.tags || []
    );
  }

  get latestRevision() {
    return this.communityRevisions[0];
  }

  get urlSlug() {
    return this.latestRevision.slug;
  }

  isReadyForViewing() {
    return this.latestRevision.isReadyForViewing();
  }

  toCommunityGridItem(): CommunityGridItem {
    return {
      communityPostId: this.communityPostId,
      communityRevisionId: this.latestRevision.communityRevisionId,
      revisionId: this.latestRevision.revisionId,
      owner: this.owner.toData(),
      urlSlug: this.urlSlug,
      fileUrl: this.latestRevision.latestFile!.fileUrl,
      // NOTE: We use the thumbnail animation for the grid page.
      animation: this.latestRevision.thumbnailAnimation,
      artboard: this.latestRevision.artboard,
      tags: this.tags,
      isRemix: this.isRemix,
      title: this.latestRevision.title,
      reactionCount: this.reactionCount,
      thumbnailUrl: this.latestRevision.thumbnailUrl,
    };
  }

  toCommunityShotItem(
    communityRevisionId?: number | undefined
  ): CommunityShotItem {
    const selectedRevision = this.getSelectedRevision(communityRevisionId);

    return {
      communityPostId: this.communityPostId,
      communityRevisionId: selectedRevision.communityRevisionId,
      revisionId: selectedRevision.revisionId,
      fileId: this.fileId,
      isReadyForViewing: selectedRevision.isReadyForViewing(),
      title: selectedRevision.title,
      description: selectedRevision.description,
      slug: selectedRevision.slug,
      urlSlug: selectedRevision.slug,
      animation: selectedRevision.animation,
      artboard: selectedRevision.artboard,
      isAnimationStateMachine: selectedRevision.isAnimationStateMachine,
      fileUrl: selectedRevision.latestFile?.fileUrl ?? null,
      thumbnailUrl: selectedRevision.thumbnailUrl,
      watermarkThumbnailUrl: selectedRevision.watermarkThumbnailUrl,
      videoUrl: selectedRevision.videoUrl,
      videoWidth: selectedRevision.videoWidth,
      videoHeight: selectedRevision.videoHeight,
      username: this.owner.username,
      avatar: this.owner.avatar,
      ownerId: this.owner.ownerId,
      isForHire: this.owner.isForHire,
      lastUpdated: selectedRevision.created.toISOString(),
      revisions: this.communityRevisions.map((cr) => cr.toShotRevision()),
      parent: this.parent?.toShotParent() ?? null,
      root: this.root?.toShotParent() ?? null,
      isRemix: this.isRemix,
      totalRemixCount: this.totalRemixCount,
      publishedRemixCount: this.publishedRemixCount,
    };
  }

  getSelectedRevision(communityRevisionId: number | undefined) {
    if (communityRevisionId === undefined) {
      return this.latestRevision;
    }

    const selected = this.communityRevisions.find(
      (r) => r.communityRevisionId === communityRevisionId
    );

    if (selected === undefined) {
      return this.latestRevision;
    }

    return selected;
  }
}

export class ParentCommunityPost {
  constructor(
    public communityPostId: number,
    public communityRevisionId: number,
    public title: string,
    public thumbnailUrl: string,
    public username: string,
    public slug: string
  ) {}

  static fromAPI(data: any): ParentCommunityPost {
    return new ParentCommunityPost(
      data.community_post_id,
      data.community_revision_id,
      data.title,
      data.thumbnail_url,
      data.username,
      data.slug
    );
  }

  toShotParent(): ShotParent {
    return {
      communityPostId: this.communityPostId,
      communityRevisionId: this.communityRevisionId,
      title: this.title,
      thumbnailUrl: this.thumbnailUrl,
      username: this.username,
      slug: this.slug,
    };
  }
}

export class CommunityOwner {
  constructor(
    public ownerId: number,
    public username: string,
    public avatar: string,
    public isForHire: boolean
  ) {}

  static fromAPI(data: any): CommunityOwner {
    return new CommunityOwner(
      data.owner_id,
      data.username,
      data.avatar,
      data.is_for_hire
    );
  }

  toData() {
    return {
      ownerId: this.ownerId,
      username: this.username,
      avatar: this.avatar,
      isForHire: this.isForHire,
    };
  }
}

export class CommunityRevision {
  constructor(
    public communityRevisionId: number,
    public revisionId: number,
    public created: Date,
    public updated: Date,
    public animation: string,
    public artboard: string,
    public title: string,
    public description: string,
    public isAnimationStateMachine: boolean,
    public thumbnailAnimation: string,
    public thumbnailUrl: string | null,
    public watermarkThumbnailUrl: string | null,
    public videoUrl: string | null,
    public videoWidth: number | null,
    public videoHeight: number | null,
    public slug: string,
    public communityFiles: CommunityFile[]
  ) {}

  static fromAPI(data: any): CommunityRevision {
    return new CommunityRevision(
      data.community_revision_id,
      data.revision_id,
      new Date(data.created),
      new Date(data.updated),
      data.animation,
      data.artboard,
      data.title,
      data.description,
      data.is_animation_state_machine,
      data.thumbnail_animation,
      data.thumbnail_url,
      data.watermark_thumbnail_url ?? null,
      data.video_url,
      data.video_width,
      data.video_height,
      data.slug,
      data.community_files.map((community_file: any) =>
        CommunityFile.fromAPI(community_file)
      )
    );
  }

  toShotRevision(): ShotRevision {
    return {
      communityRevisionId: this.communityRevisionId,
      revisionId: this.revisionId,
      lastUpdated: this.created.toISOString(),
      isReadyForViewing: this.isReadyForViewing(),
      slug: this.slug,
      title: this.title,
    };
  }

  isReadyForViewing() {
    return !!this.latestFile;
  }

  get latestFile() {
    if (this.communityFiles.length === 0) {
      return null;
    }
    return this.communityFiles[0];
  }
}

export class CommunityFile {
  constructor(
    public communityFileId: number,
    public created: Date,
    public updated: Date,
    public fileUrl: string,
    public runtimeMajorVersion: number
  ) {}

  static fromAPI(data: any): CommunityFile {
    return new CommunityFile(
      data.community_file_id,
      new Date(data.created),
      new Date(data.updated),
      data.file_url,
      data.runtime_major_version
    );
  }
}

export type CommunityGridItem = {
  communityPostId: number;
  communityRevisionId: number;
  revisionId: number;
  owner: {
    ownerId: number;
    username: string;
    avatar: string;
    isForHire?: boolean;
  };
  title: string;
  urlSlug: string;
  fileUrl: string;
  animation: string;
  artboard: string;
  tags: string[];
  isRemix: boolean;
  reactionCount: number;
  thumbnailUrl: string | null;
};

export type CommunityShotItem = {
  communityPostId: number;
  communityRevisionId: number;
  revisionId: number;
  fileId: number;
  isReadyForViewing: boolean;
  title: string;
  description: string;
  slug: string;
  urlSlug: string;
  artboard: string;
  animation: string;
  isAnimationStateMachine: boolean;
  fileUrl: string | null;
  thumbnailUrl: string | null;
  watermarkThumbnailUrl: string | null;
  videoUrl: string | null;
  videoWidth: number | null;
  videoHeight: number | null;
  username: string;
  avatar: string | null;
  ownerId: number;
  isForHire: boolean;
  lastUpdated: string;
  revisions: ShotRevision[];
  parent: ShotParent | null;
  root: ShotParent | null;
  isRemix: boolean;
  totalRemixCount: number;
  publishedRemixCount: number;
};

export type ShotRevision = {
  communityRevisionId: number;
  revisionId: number;
  lastUpdated: string;
  isReadyForViewing: boolean;
  slug: string;
  title: string;
};

export type ShotParent = {
  communityPostId: number;
  communityRevisionId: number;
  title: string;
  thumbnailUrl: string;
  username: string;
  slug: string;
};

export type CommunityUserProfile = {
  ownerId: number;
  username: string;
  name: string | null;
  avatar: string | null;
  blurb: string | null;
  location: string | null;
  website: string | null;
  isForHire: boolean;
  social: {
    twitter: string | null;
    instagram: string | null;
    linkedin: string | null;
    medium: string | null;
    github: string | null;
    behance: string | null;
    vimeo: string | null;
    dribbble: string | null;
  };
};

export class UserProfile {
  constructor(
    public owner_id: number,
    public username: string,
    public avatar: string | null,
    public name: string | null,
    public website: string | null,
    public blurb: string | null,
    public location: string | null,
    public twitter: string | null,
    public instagram: string | null,
    public github: string | null,
    public behance: string | null,
    public linkedin: string | null,
    public medium: string | null,
    public vimeo: string | null,
    public dribbble: string | null,
    public isForHire: boolean
  ) {}

  static fromAPI(data: any): UserProfile {
    return new UserProfile(
      data.owner_id,
      data.username,
      data.avatar,
      data.name,
      data.website,
      data.blurb,
      data.location,
      data.twitter,
      data.instagram,
      data.github,
      data.behance,
      data.linkedin,
      data.medium,
      data.vimeo,
      data.dribbble,
      data.is_for_hire
    );
  }

  toCommunityUserProfile(): CommunityUserProfile {
    return {
      ownerId: this.owner_id,
      username: this.username,
      name: this.name,
      avatar: this.avatar,
      blurb: this.blurb,
      location: this.location,
      website: this.website,
      isForHire: this.isForHire,
      social: {
        twitter: this.twitter,
        instagram: this.instagram,
        github: this.github,
        behance: this.behance,
        linkedin: this.linkedin,
        medium: this.medium,
        vimeo: this.vimeo,
        dribbble: this.dribbble,
      },
    };
  }
}

export type CommunityIds = {
  communityPostId: number | undefined;
  communityRevisionId: number | undefined;
};

export type CommunityReactionUser = {
  username: string;
  avatar: string | null;
};

export type CommunityReactionDetails = {
  name: string;
  reactionId: number;
  count: number;
  users: CommunityReactionUser[];
  isReactedTo: boolean;
};

export class CommunityReactions {
  static order = ['mindblown', 'joy', 'tada', 'bullseye', 'onfire', 'love'];

  constructor(public reactions: Record<string, CommunityReactionDetails>) {}

  static fromAPI(data: any): CommunityReactions {
    const reactionData = Object.entries(data).reduce((acc, entry) => {
      const [name, reactionDetails] = entry;
      acc[name] = {
        name,
        reactionId: (reactionDetails as any).reaction_id,
        count: (reactionDetails as any).count,
        users: (reactionDetails as any).users,
        isReactedTo: (reactionDetails as any).is_reacted_to,
      };
      return acc;
    }, {} as Record<string, CommunityReactionDetails>);

    return new CommunityReactions(reactionData);
  }

  /**
   * Returns a list of reactions in the intended order to display.
   */
  asOrderedList(): CommunityReactionDetails[] {
    return CommunityReactions.order.map((name) => this.reactions[name]);
  }

  /**
   * Returns the sum of all reaction counts.
   */
  getTotalCount(): number {
    return Object.values(this.reactions).reduce(
      (total, reaction) => total + reaction.count,
      0
    );
  }

  /**
   * Returns a list of reaction names that have had a reaction.
   */
  getSortedNames() {
    // This sorted the reactions into an order based on their count
    // however reactions that moved backward would stop animating.
    //
    // Not essential for MVP, but would like to dig into this and
    // get the reordering going again at some point.

    // const reactions = Object.keys(count)
    //   .sort((a, b) => count[a] - count[b])
    //   .filter((e) => count[e] > 0)
    //   .reverse();

    return Object.keys(this.reactions).filter(
      (reaction) => this.reactions[reaction].count > 0
    );
  }

  /**
   * Returns a {CommunityReactionDetails} object based on the reaction names passed.
   *
   * @param name Name of reaction to get details for.
   * @returns
   */
  getByName(name: string): CommunityReactionDetails {
    if (this.reactions[name] === undefined) {
      throw new Error(`Reaction not found: ${name}`);
    }
    return this.reactions[name];
  }

  /**
   * Returns true if the user has reacted to any reaction.
   */
  hasUserReacted() {
    return Object.values(this.reactions).some((r) => r?.isReactedTo);
  }

  /**
   * Returns the reaction that the current user has reacted to.
   *
   * Returns undefined if the user has not reacted to any.
   */
  getReactedToReaction() {
    return this.asOrderedList().find((r) => r?.isReactedTo);
  }
}

export type ShotRemix = {
  communityPostId: number;
  communityRevisionId: number;
  title: string;
  thumbnailUrl: string;
  thumbnailAnimation: string;
  artboard: string;
  fileUrl: string;
  slug: string;
  username: string;
  avatar: string;
  ownerId: number;
  reactionCount: number;
};

export class CommunityRemix {
  constructor(
    public communityPostId: number,
    public communityRevisionId: number,
    public title: string,
    public thumbnailUrl: string,
    public thumbnailAnimation: string,
    public artboard: string,
    public fileUrl: string,
    public slug: string,
    public username: string,
    public avatar: string,
    public ownerId: number,
    public reactionCount: number
  ) {}

  static fromAPI(data: any): CommunityRemix {
    return new CommunityRemix(
      data.community_post_id,
      data.community_revision_id,
      data.title,
      data.thumbnail_url,
      data.thumbnail_animation,
      data.artboard,
      data.file_url,
      data.slug,
      data.username,
      data.avatar,
      data.owner_id,
      data.reaction_count ?? 0
    );
  }

  toShotRemix(): ShotRemix {
    return {
      communityPostId: this.communityPostId,
      communityRevisionId: this.communityRevisionId,
      title: this.title,
      thumbnailUrl: this.thumbnailUrl,
      thumbnailAnimation: this.thumbnailAnimation,
      artboard: this.artboard,
      fileUrl: this.fileUrl,
      slug: this.slug,
      username: this.username,
      avatar: this.avatar,
      ownerId: this.ownerId,
      reactionCount: this.reactionCount,
    };
  }
}

export type CommunityUserForHireListItem = {
  ownerId: number;
  username: string;
  avatar: string;
  location: string | null;
  recentPosts: CommunityGridItem[];
};

export class CommunityUserForHire {
  constructor(
    public ownerId: number,
    public username: string,
    public avatar: string,
    public location: string | null,
    public recentPosts: CommunityPost[]
  ) {}

  static fromAPI(data: any): CommunityUserForHire {
    return new CommunityUserForHire(
      data.owner_id,
      data.username,
      data.avatar,
      data.location,
      data.recent_posts
        .filter((post: any) => post !== null)
        .map((post: any) => CommunityPost.fromAPI(post))
    );
  }

  toListItem(): CommunityUserForHireListItem {
    return {
      ownerId: this.ownerId,
      username: this.username,
      avatar: this.avatar,
      location: this.location,
      recentPosts: this.recentPosts.map((post: any) =>
        post.toCommunityGridItem()
      ),
    };
  }
}
